/** @forge-header
 * @id          gameBuilder
 * @role        Pure game construction | grouped events + orderbooks → GameData structs
 * @pattern     pure-fn
 * @purity      none
 * @units       cents
 * @upstream    sportsDiscovery:GroupedEvent | sportsStream/types:MoneylineGameData,SpreadGameData,TotalGameData,MarketBook,BookLevel
 * @downstream  sportsStream/stream (rebuildGames, initialize)
 * @exports     mapConcurrent:fn | parseTeamNamesFromTitle:fn | orderbookToLevels:fn | buildMoneylineGame:fn | buildSpreadGame:fn | buildTotalGame:fn
 * @test        stream.test.ts (integration-level via createSportsStream)
 */

import type { GroupedEvent } from '../sportsDiscovery';
import type {
  MoneylineGameData,
  SpreadGameData,
  TotalGameData,
  MarketBook,
  SpreadMarketInfo,
  BookLevel,
} from './types';

/** Run async tasks with a concurrency limit */
export async function mapConcurrent<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      results[i] = await fn(items[i]);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()));
  return results;
}

/**
 * Parse team names from event title.
 * Format: "{Away} at {Home}: Spread" or "{Away} at {Home}"
 */
export function parseTeamNamesFromTitle(
  title: string
): { awayName: string; homeName: string } | null {
  // Try with suffix (": Spread", ": Total", etc.)
  let match = title.match(/^(.+?) at (.+?)(?::|$)/);
  if (match && match[1] && match[2]) {
    return { awayName: match[1].trim(), homeName: match[2].trim() };
  }
  // Try without suffix
  match = title.match(/^(.+?) at (.+)$/);
  if (match && match[1] && match[2]) {
    return { awayName: match[1].trim(), homeName: match[2].trim() };
  }
  return null;
}

/** Convert raw orderbook to BookLevel array */
export function orderbookToLevels(raw: Array<[number, number]> | null | undefined): BookLevel[] {
  if (!Array.isArray(raw)) return [];
  const out: BookLevel[] = [];
  for (const rec of raw) {
    if (!Array.isArray(rec) || rec.length < 2) continue;
    const [p, q] = rec;
    const priceCents = Number(p);
    const size = Number(q);
    if (!Number.isFinite(priceCents) || !Number.isFinite(size)) continue;
    if (size <= 0) continue;
    out.push({ priceCents, size });
  }
  // Ensure descending by price
  out.sort((a, b) => b.priceCents - a.priceCents);
  return out;
}

export function normalizeParticipantName(name: string | null | undefined): string {
  return String(name ?? '')
    .normalize('NFKD')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Build MoneylineGameData from grouped event */
export function buildMoneylineGame(
  event: GroupedEvent,
  marketBooks: Map<string, MarketBook>
): MoneylineGameData {
  let awayBook: MarketBook | null = null;
  let homeBook: MarketBook | null = null;

  // PRIMARY: name-based matching (reliable for all sports, especially CBB where
  // ticker code splits are ambiguous). Uses display names from yes_sub_title.
  if (event.awayName && event.homeName) {
    const awayNameKey = normalizeParticipantName(event.awayName);
    const homeNameKey = normalizeParticipantName(event.homeName);
    for (const dm of event.markets.values()) {
      const outcomeNameKey = normalizeParticipantName(dm.outcomeName);
      if (!outcomeNameKey) continue;
      if (!awayBook && outcomeNameKey === awayNameKey) {
        awayBook = marketBooks.get(dm.marketTicker) ?? null;
      }
      if (!homeBook && outcomeNameKey === homeNameKey) {
        homeBook = marketBooks.get(dm.marketTicker) ?? null;
      }
      if (awayBook && homeBook) break;
    }
  }

  // FALLBACK: code-based matching (works for unambiguous sports like NBA/NHL)
  if (!awayBook) awayBook = marketBooks.get(event.awayCode) ?? null;
  if (!homeBook) homeBook = marketBooks.get(event.homeCode) ?? null;

  return {
    marketType: 'moneyline',
    eventTicker: event.eventTicker,
    date: event.dateYyyyMmDd,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: event.awayName,
    homeName: event.homeName,
    startTimePt: null, // Will be fetched from event API
    away: awayBook,
    home: homeBook,
    polymarket: null,
  };
}

/** Build SpreadGameData from grouped event */
export function buildSpreadGame(
  event: GroupedEvent,
  marketBooks: Map<string, MarketBook>
): SpreadGameData {
  const markets = new Map<string, SpreadMarketInfo>();

  for (const [outcomeKey, dm] of event.markets.entries()) {
    const book = marketBooks.get(dm.marketTicker);
    if (book) {
      // Key format: "{teamCode}-{bucket}" e.g., "NYK-7"
      const key =
        dm.spreadBucket !== undefined ? `${dm.outcomeCode}-${dm.spreadBucket}` : outcomeKey;

      // Get floor_strike from discovered market (more accurate than bucket-based calculation)
      const floorStrike =
        dm.floorStrike ?? (dm.spreadBucket !== undefined ? dm.spreadBucket + 0.5 : 0);

      markets.set(key, {
        book,
        floorStrike,
        teamCode: dm.outcomeCode ?? '',
      });
    }
  }

  return {
    marketType: 'spread',
    eventTicker: event.eventTicker,
    date: event.dateYyyyMmDd,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: event.awayName,
    homeName: event.homeName,
    startTimePt: null,
    markets,
    polymarketMain: null,
  };
}

/** Build TotalGameData from grouped event */
export function buildTotalGame(
  event: GroupedEvent,
  marketBooks: Map<string, MarketBook>
): TotalGameData {
  const markets = new Map<number, MarketBook>();

  for (const [_outcomeKey, dm] of event.markets.entries()) {
    const book = marketBooks.get(dm.marketTicker);
    // Prefer floor_strike for precise line values (e.g., 210.5) and fallback to ticker strike.
    const preciseLine = dm.floorStrike ?? dm.totalStrike;
    if (book && preciseLine !== undefined && Number.isFinite(preciseLine)) {
      markets.set(preciseLine, book);
    }
  }

  return {
    marketType: 'total',
    eventTicker: event.eventTicker,
    date: event.dateYyyyMmDd,
    awayCode: event.awayCode,
    homeCode: event.homeCode,
    awayName: event.awayName,
    homeName: event.homeName,
    startTimePt: null,
    markets,
    polymarketMain: null,
  };
}
